import { useEffect, useState } from "react";
import { X, Copy, CheckCircle2, Smartphone, MessageCircle } from "lucide-react";
import { useStore, buildWhatsappLink } from "../lib/store";

/**
 * Modal de depósito por Yape.
 * Muestra QR + número Yape + monto, y al confirmar abre WhatsApp para enviar comprobante.
 *
 * props:
 *  - open: boolean
 *  - onClose: () => void
 *  - reservationData: { nombre, telefono, email, serviceLabel, fecha, hora } | null
 */
export const YapeModal = ({ open, onClose, reservationData }) => {
  const { business } = useStore();
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const yape = business.yape || {};
  const amount = yape.depositAmount || 30;

  const copy = (key, value) => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(""), 1800);
    });
  };

  const sendProof = () => {
    const data = reservationData || {};
    const msg =
      `Hola AURA! Realicé el depósito por Yape de S/ ${amount} para confirmar mi cita.\n\n` +
      (data.nombre ? `• Nombre: ${data.nombre}\n` : "") +
      (data.serviceLabel ? `• Servicio: ${data.serviceLabel}\n` : "") +
      (data.fecha ? `• Fecha: ${data.fecha}\n` : "") +
      (data.hora ? `• Hora: ${data.hora}\n` : "") +
      `\nAdjunto la captura del pago. ¡Gracias!`;
    const link = buildWhatsappLink(business.whatsappNumberOnly || business.whatsapp, msg);
    window.open(link, "_blank");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6 bg-ink/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      data-testid="yape-modal"
    >
      <div
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#702f8a] to-[#4f1f6b] text-white px-6 py-7 text-center">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            data-testid="yape-modal-close"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
          <div className="inline-flex items-center gap-1.5 mb-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm">
            <Smartphone size={12} />
            <span className="text-[10px] tracking-luxe uppercase font-medium">Yape</span>
          </div>
          <div className="font-serif text-3xl">Pagar depósito</div>
          <div className="font-serif text-5xl mt-1">S/ {amount}</div>
          <div className="text-xs text-white/80 mt-2 max-w-xs mx-auto">{yape.note || "Depósito para confirmar tu cita."}</div>
        </div>

        {/* QR */}
        <div className="px-6 py-6 text-center">
          <div className="inline-block p-3 rounded-2xl bg-white border-2 border-beige-200">
            <img
              src={yape.qr}
              alt="QR Yape AURA"
              className="w-48 h-48 object-contain"
              data-testid="yape-modal-qr"
            />
          </div>
          <div className="text-xs text-ink-soft mt-3 font-light">Escanea con tu app Yape</div>
        </div>

        {/* Info Yape */}
        <div className="px-6 pb-6 space-y-2">
          <CopyRow
            label="Número Yape"
            value={yape.phone || business.whatsapp}
            copied={copied === "phone"}
            onCopy={() => copy("phone", (yape.phone || "").replace(/\D/g, ""))}
            testid="yape-copy-phone"
          />
          <CopyRow
            label="Titular"
            value={yape.holder || business.director}
            copied={copied === "holder"}
            onCopy={() => copy("holder", yape.holder || business.director)}
            testid="yape-copy-holder"
          />
          <CopyRow
            label="Monto"
            value={`S/ ${amount}`}
            copied={copied === "amount"}
            onCopy={() => copy("amount", String(amount))}
            testid="yape-copy-amount"
          />
        </div>

        {/* Pasos */}
        <div className="px-6 pb-4">
          <div className="text-xs tracking-luxe uppercase text-ink-soft mb-3">Instrucciones</div>
          <ol className="text-sm text-ink-soft space-y-2 font-light list-decimal list-inside">
            <li>Abre tu app Yape y escanea el QR (o usa el número).</li>
            <li>Paga el monto exacto de <span className="text-ink font-medium">S/ {amount}</span>.</li>
            <li>Toma captura del comprobante.</li>
            <li>Pulsa "Enviar comprobante" para confirmar por WhatsApp.</li>
          </ol>
        </div>

        {/* CTA */}
        <div className="px-6 pb-6">
          <button
            onClick={sendProof}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#25D366] text-white text-xs tracking-luxe uppercase font-medium hover:brightness-110 transition-all"
            data-testid="yape-send-proof"
          >
            <MessageCircle size={16} />
            Enviar comprobante por WhatsApp
          </button>
          <button
            onClick={onClose}
            className="w-full mt-2 text-xs text-ink-soft hover:text-ink py-2 transition-colors"
            data-testid="yape-modal-cancel"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

const CopyRow = ({ label, value, copied, onCopy, testid }) => (
  <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-beige-50 border border-beige-200">
    <div className="min-w-0 flex-1">
      <div className="text-[10px] tracking-luxe uppercase text-ink-soft">{label}</div>
      <div className="text-sm text-ink font-medium truncate" data-testid={`${testid}-value`}>{value}</div>
    </div>
    <button
      onClick={onCopy}
      data-testid={testid}
      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${copied ? "bg-gold text-white" : "bg-white border border-beige-300 text-ink-soft hover:border-gold hover:text-gold"}`}
      aria-label="Copiar"
    >
      {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
    </button>
  </div>
);
